import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { Snackbar, Button, Chip, IconButton, Box, Typography, Paper, Dialog, DialogContent, DialogActions, TextField, CircularProgress } from '@mui/material';
import DataTable from "../DataTable/DataTable";
import { styled, alpha } from "@mui/material/styles";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import CommonButton from "../CommonButton/CommonButton";
import Alert from "@mui/material/Alert"; // Import Alert for better styling
import Menu, { MenuProps } from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheck";
import { fetchAllReportsData, fetchReportsByPartner, mapActivityRequestStatusCodeToLabel } from "../../../services/activityReportService";
import { useAuth } from "../../auth/AuthProvider";

// Icons
import EditIcon from "@mui/icons-material/Edit";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color: "rgb(55, 65, 81)",
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
        ...theme.applyStyles("dark", {
          color: "inherit",
        }),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity,
        ),
      },
    },
    ...theme.applyStyles("dark", {
      color: theme.palette.grey[300],
    }),
  },
}));

const ReportsTable = forwardRef(
  ({ searchText, tableRef, isScheduled, showHeader, onUpcomingReportsChange,
    title,
    message,
    severity = 'success',
    onClose,
    onUndo = true,
    onRetry,
    showIcon = true,
    actionButtons = [],
    autoHideDuration = 6000,
    anchorOrigin = { vertical: 'top', horizontal: 'right' },
  }, ref) => {

    const iconMap = {
      success: CheckCircleIcon,
      error: ErrorIcon,
      warning: WarningIcon,
      info: InfoIcon,
    };

    const navigate = useNavigate();
    const apiRef = useGridApiRef();
    const { user, role } = useAuth();

    // Menu state
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [selectedRow, setSelectedRow] = React.useState(null);
    const open = Boolean(anchorEl);

    // Snackbar state - Simplified version
    const [snackbar, setSnackbar] = useState({
      open: false,
      message: "",
      severity: "success", // 'success', 'error', 'info', 'warning'
    });

    const [snackbar2, setSnackbar2] = useState({
      open: false,
      title: "",
      message: "",
      severity: "info",
      actionButtons: [],
    });

    const IconComponent = iconMap[severity];

    const getIconColor = () => {
      const colors = {
        success: '#005786',
        error: '#f44336',
        warning: '#ff9800',
        info: '#2196f3',
      };
      return colors[severity];
    };

    /* =======================
    2. STATE
    ======================= */
    const [reports, setReports] = useState([]);
    const [upcomingReportsCount, setUpcomingReportsCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true); // ADD THIS
    const [openingReportId, setOpeningReportId] = useState(null);

    // Copy: if role is partner then achievedStatus is "Submitted" if role is operations then achievedStatus is "Complete"
    const [achievedStatus, setAchievedStatus] = useState(role === "partner" ? "Submitted" : "Complete");
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const [duplicateForm, setDuplicateForm] = useState({
      title: '',
      due_date: '',
      period_start_date: '',
      period_end_date: '',
    });

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4200/api';

    /* =======================
    2.5 HELPER FUNCTION - STATUS COLOR MAPPING
    ======================= */
    const getStatusColor = (status) => {
      const colorMap = {
        Draft: { backgroundColor: "#E0E3E5", color: "#757677" },
        Scheduled: { backgroundColor: "#E0E3E5", color: "#757677" },
        "In Progress": { backgroundColor: "#F7E3BA", color: "#735417" },
        "New Request": { backgroundColor: "#ECDFF2", color: "#4D2966" },
        Overdue: { backgroundColor: "#F7D5D2", color: "#BF4539" },
        Completed: { backgroundColor: "#E8F5E9", color: "#2E7D32" },
        Complete: { backgroundColor: "#E8F5E9", color: "#2E7D32" },
        Submitted: { backgroundColor: "#E8F5E9", color: "#2E7D32" },
      };
      return (
        colorMap[status] || { backgroundColor: "#F5F5F5", color: "#616161" }
      );
    };



    const StyledSnackbarWithTitle = styled(Snackbar)(({ theme, severity }) => ({
      '& .MuiSnackbarContent-root': {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderRadius: '12px',
        boxShadow: theme.shadows[8],
        padding: '16px 20px',
        minWidth: '300px',
        maxWidth: '380px',
        border: `1px solid ${theme.palette.divider}`,


        // Severity-based left border accent
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '4px',
          backgroundColor: severity === 'error'
            ? theme.palette.error.main
            : severity === 'success'
              ? theme.palette.success.main
              : severity === 'warning'
                ? theme.palette.warning.main
                : theme.palette.info.main,
        },
      },
    }));

    const SnackbarContent = styled(Box)(({ theme }) => ({
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      width: '100%',
    }));

    const ContentWrapper = styled(Box)({
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    });

    const TitleTypography = styled(Typography)(({ theme, severity }) => ({
      fontWeight: 600,
      fontSize: '1rem',
      lineHeight: 1.5,
      color: severity === 'error'
        ? theme.palette.error.main
        : severity === 'success'
          ? theme.palette.success.main
          : severity === 'warning'
            ? theme.palette.warning.main
            : theme.palette.info.main,
    }));

    const MessageTypography = styled(Typography)(({ theme }) => ({
      fontSize: '0.875rem',
      color: theme.palette.text.secondary,
      lineHeight: 1.5,
    }));

    const ActionWrapper = styled(Box)({
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
      marginTop: '8px',
    });

    const fetch2ReportsData = async () => {
      setIsLoading(true); // ADD THIS
      try {
        let data = '';

        if (role === "partner") {
          data = await fetchReportsByPartner("Rob M. Stavis", "RMS");
          //console.log("Fetched reports data by partner: ", data);
        } else {
          data = await fetchAllReportsData();
          //console.log("Fetched all reports data: ", data);
        }


        // const data = [];
        if (isScheduled) {
          // For scheduled reports, we can filter the data to only include those with status "Scheduled"

          //const data = await fetchReportsData();

          // mock data that will be added to data object.
          const scheduledMockData = [
            // {
            //   id: "aeff9512-8c38-4066-bb6d-93f2f0e580ca",
            //   report_name: "Quarterly report AAA",
            //   request_type: 2,
            //   status: 2,
            //   period_start_date: "2026-05-01T15:22:33.559Z",
            //   period_end_date: "2026-08-31T15:22:33.559Z",
            //   completed_date: null,
            //   report_sent_on: "2026-02-25T01:40:12.214Z",
            //   due_date: "2026-04-28T05:00:00.000Z",
            //   created_at: "2026-02-25T01:40:12.214Z",
            //   updated_at: "2026-02-25T01:40:12.214Z",
            // },
            // {
            //   id: "c28e37e5-e826-4083-90d2-3771966bbb02",
            //   report_name: "Monthly report AA",
            //   request_type: 1,
            //   status: 2,
            //   period_start_date: "2026-03-01T05:00:00.000Z",
            //   period_end_date: "2026-03-31T04:59:59.999Z",
            //   completed_date: null,
            //   report_sent_on: "2026-02-24T18:55:01.019Z",
            //   due_date: "2026-04-27T00:00:00.000Z",
            //   created_at: "2026-02-24T18:55:01.019Z",
            //   updated_at: "2026-02-24T18:55:01.019Z",
            // },
            // {
            //   id: "c28e37e5-e826-4083-90d2-3771916beb02",
            //   report_name: "Monthly report AA",
            //   request_type: 1,
            //   status: 2,
            //   period_start_date: "2026-04-01T05:00:00.000Z",
            //   period_end_date: "2026-04-30T04:59:59.999Z",
            //   completed_date: null,
            //   report_sent_on: "2026-02-24T18:55:01.019Z",
            //   due_date: "2026-04-27T00:00:00.000Z",
            //   created_at: "2026-02-24T18:55:01.019Z",
            //   updated_at: "2026-02-24T18:55:01.019Z",
            // },
          ];

          // Append mock data to the fetched data
          data.data = [...data.data, ...scheduledMockData];

          // if request type is Monthly and start date is on the next month in comparison with current date, then status should be Scheduled (5) and should be added to the table. This is just for demonstration purposes until we have real scheduled data from the backend. In real scenario, the backend should return the correct status based on the due date and current date.
          const currentDate = new Date();
          const currentQuarter = Math.ceil((currentDate.getMonth() + 1) / 3);

          const updatedData = data.data.map((item) => {
            if (item.request_type === 1) {
              console.log("Checking monthly report: ", item);
              const startDate = new Date(item.period_start_date);
              if (startDate.getMonth() > currentDate.getMonth() && startDate.getFullYear() >= currentDate.getFullYear()) {
                return { ...item, status: 5 }; // Set status to "Scheduled"
              }
            }
            // if request type is Quarterly and start date is on the next quarter in comparison with current date, then status should be Scheduled (5) and should be added to the table. This is just for demonstration purposes until we have real scheduled data from the backend. In real scenario, the backend should return the correct status based on the due date and current date.
            if (item.request_type === 2) {
              const startDate = new Date(item.period_start_date);
              const startQuarter = Math.ceil((startDate.getMonth() + 1) / 3);
              console.log(
                "Start Quarter: ",
                startQuarter,
                "Current Quarter: ",
                currentQuarter,
              );
              // If the start quarter is greater than the current quarter and year is greater that current year, we consider it as scheduled. This is a simplification for demonstration purposes. In a real scenario, the backend should determine the status based on the due date and current date.
              if (startQuarter > currentQuarter && startDate.getFullYear() >= currentDate.getFullYear()) {
                return { ...item, status: 5 }; // Set status to "Scheduled"
              }
            }

            //if request type is annual and start date is on the next year in comparison with current date, then status should be Scheduled (5) and should be added to the table. This is just for demonstration purposes until we have real scheduled data from the backend. In real scenario, the backend should return the correct status based on the due date and current date.
            if (item.request_type === 3) {
              const startDate = new Date(item.period_start_date);
              if (startDate.getFullYear() > currentDate.getFullYear()) {
                return { ...item, status: 5 }; // Set status to "Scheduled"
              }
            }

            if (item.request_type === 4) {
              return { ...item, status: 3 };
            }

            return item;
          });

          //   console.log("Fetched reports data: ", data);

          let parsedData = updatedData
            .filter((item) => item.status === 5) // Assuming status code 5 corresponds to "Scheduled"
            .map((item) => {
              if (item.due_date < new Date().toISOString()) {
                item.status = 0; // Mark as "Overdue" if past due date
              }
              return {
                id: item.id,
                quarter: `Q${Math.ceil((new Date(item.period_end_date).getMonth() + 1) / 3)} ${new Date(item.period_end_date).getFullYear()}`,
                request:
                  item.request_type === 1
                    ? "Monthly"
                    : item.request_type === 2
                      ? "Quarterly"
                      : item.request_type === 3
                        ? "Annual"
                        : "Custom",
                name: item.report_name,
                period: `${new Date(item.period_start_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${new Date(item.period_end_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
                sent: item.report_sent_on
                  ? new Date(item.report_sent_on).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                  : "---",
                dueDate: item.due_date
                  ? new Date(item.due_date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                  : "---",
                completionDate: item.completed_date
                  ? new Date(item.completed_date).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })
                  : "---",
                status: mapActivityRequestStatusCodeToLabel(item.status, role), // Convert status code to label
                // 🔥 ADD THESE (FINAL FIX)
                settingsData: item,
                company:
                  item.company?.length ? item.company :       // direct root field (saved by Step2/Step3)
                    item.request_data?.company?.length ? item.request_data.company :
                      item.processPartnersData ||
                      item.partnersData ||
                      item.partners ||
                      [],
                request_data: item.request_data || [],
              };
            });

          setReports(parsedData);
          setIsLoading(false);

          const count = parsedData.length;
          setUpcomingReportsCount(count);

          // Call the callback prop to send count to parent
          if (onUpcomingReportsChange) {
            onUpcomingReportsChange(count);
          }
        } else { // Actual reports table (not scheduled)
          // console.log("Fetched reports data J: ", data);
          // If request type is Monthly and start date is on the current month in comparison with current date, then status should be In Progress (2). If request type is Quarterly and start date is on the current quarter in comparison with current date, then status should be In Progress (2). If request type is Annual and start date is on the current year in comparison with current date, then status should be In Progress (2). This is just for demonstration purposes until we have real data from the backend with correct statuses based on the due date and current date. In real scenario, the backend should return the correct status based on the due date and current date.
          const actualAssetsData = data.data.reduce((acc, item) => {
            const currentDate = new Date();
            const currentQuarter = Math.ceil((currentDate.getMonth() + 1) / 3);

            if (item.request_type === 1) {
              const startDate = new Date(item.period_start_date);
              if (
                startDate.getMonth() === currentDate.getMonth() &&
                startDate.getFullYear() === currentDate.getFullYear()
              ) {
                acc.push({ ...item, status: 3 }); // Set status to "New Request"
              } else {
                console.log("Filtering out monthly report: ", item);
              }
            } else if (item.request_type === 2) {
              const startDate = new Date(item.period_start_date);
              if (
                currentQuarter === Math.ceil((startDate.getMonth() + 1) / 3) &&
                startDate.getFullYear() === currentDate.getFullYear()
              ) {
                acc.push({ ...item, status: 3 }); // Set status to "In Progress"
              }
            } else if (item.request_type === 3) {
              const startDate = new Date(item.period_start_date);
              if (startDate.getFullYear() === currentDate.getFullYear()) {
                acc.push({ ...item, status: 3 }); // Set status to "In Progress"
              }
            } else if (item.request_type === 4) {
              // For custom reports, we can determine the status based on the due date and current date. If due date is in the future, we set it as "In Progress", if due date is in the past and report is not completed, we set it as "Overdue", if report is completed, we set it as "Completed". This is a simplification for demonstration purposes. In a real scenario, the backend should determine the status based on the due date and current date.
              const dueDate = new Date(item.due_date);
              acc.push({
                ...item,
                status: 3
              })
            }

            return acc;
          }, []);

          console.log("Actual assets data after status update: ", actualAssetsData);

          //const roleBaseData = role === "partner" ? actualAssetsData : data.data;
          const roleBaseData = data.data;

          let parsedData = roleBaseData.map((item) => {
            if (item.due_date < new Date().toISOString()) {
              item.status = 0;
            }
            console.log("FULL ITEM FROM API:", item);

            return {
              id: item.id,
              quarter: `Q${Math.ceil((new Date(item.period_end_date).getMonth() + 1) / 3)} ${new Date(item.period_end_date).getFullYear()}`,
              request:
                item.request_type === 1
                  ? "Monthly"
                  : item.request_type === 2
                    ? "Quarterly"
                    : item.request_type === 3
                      ? "Annual"
                      : "Custom",
              name: item.report_name,
              period: `${new Date(item.period_start_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${new Date(item.period_end_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`,
              sent: item.report_sent_on
                ? new Date(item.report_sent_on).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })
                : "---",
              dueDate: item.due_date
                ? new Date(item.due_date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })
                : "---",
              completionDate: item.completed_date
                ? new Date(item.completed_date).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })
                : "---",
              status: mapActivityRequestStatusCodeToLabel(item.status, role),
              // 🔥 ADD THESE 3 LINES (MOST IMPORTANT)
              settingsData: item,
              company:
                item.company?.length ? item.company :       // direct root field (saved by Step2/Step3)
                  item.request_data?.company?.length ? item.request_data.company :
                    item.processPartnersData ||
                    item.partnersData ||
                    item.partners ||
                    [],
              request_data: item.request_data || [],
            };
          });

          // Remove from parsedData items with status "4" (Draft) if role is partner, but keep them if role is operations
          if (role === "partner") {
            parsedData = parsedData.filter((item) => item.status !== "Draft");
          }
          setReports(parsedData);
          setIsLoading(false);  // ADD THIS
        }
      } catch (error) {
        console.error("Error fetching reports data:", error);
        setIsLoading(false); // ADD THIS
      }
    };

    // const fetch2ReportsByPartner = async (partnerName) => {
    //     try {
    //         const data = await fetchReportsByPartner(partnerName);
    //         console.log("Fetched reports data by partner: ", data);
    //     } catch (error) {            
    //         console.error("Error fetching reports data by partner:", error);
    //     }}

    useEffect(() => {
      if (isScheduled && onUpcomingReportsChange) {
        onUpcomingReportsChange(reports.length);
      }
    }, [reports, isScheduled, onUpcomingReportsChange]);

    useEffect(() => {
      fetch2ReportsData();
    }, []);

    const filteredReports = useMemo(() => {
      if (!searchText) return reports;

      const query = searchText.toLowerCase();
      return reports.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(query),
        ),
      );
    }, [reports, searchText]);

    /* =======================
    3. MENU HANDLERS
    ======================= */
    const handleMenuClick = (event, row) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
      setSelectedRow(row);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
      setSelectedRow(null);
    };

    const handleEdit = () => {
      if (selectedRow) {
        // Navigate to edit page
        navigate(`/grade-reports/${selectedRow.id}/edit`, {
          state: {
            reportName: selectedRow.name,
          },
        });
      }
      handleMenuClose();
    };

    const handleMarkAsComplete = () => {
      if (selectedRow) {
        // Update the status of the selected row to "Completed"
        setReports((prevReports) =>
          prevReports.map((report) =>
            report.id === selectedRow.id
              ? {
                ...report,
                status: "Completed",
                completionDate: new Date().toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                }),
              }
              : report,
          ),
        );
      }

      setSnackbar2({
        open: true,
        severity: "success",
        message: `"${selectedRow.name}" marked as complete successfully.`,
        title: "Marked as complete",
        actionButtons: [
          {
            label: "Undo",
            onClick: () => {
              // Revert the status back to previous state (for simplicity, we set it to "In Progress")
              setReports((prevReports) =>
                prevReports.map((report) =>
                  report.id === selectedRow.id
                    ? { ...report, status: "In Progress", completionDate: "---" }
                    : report
                )
              );
              setSnackbar2((prev) => ({ ...prev, open: false }));
            },
          },
        ],
      })


      handleMenuClose();
    };

    const handleDuplicate = () => {
      if (selectedRow) {
        setDuplicateForm({
          title: `${selectedRow.name} (Copy)`,
          due_date: selectedRow.settingsData?.due_date
            ? new Date(selectedRow.settingsData.due_date).toISOString().slice(0, 10)
            : '',
          period_start_date: selectedRow.settingsData?.period_start_date
            ? new Date(selectedRow.settingsData.period_start_date).toISOString().slice(0, 10)
            : '',
          period_end_date: selectedRow.settingsData?.period_end_date
            ? new Date(selectedRow.settingsData.period_end_date).toISOString().slice(0, 10)
            : '',
          _rowId: selectedRow.id,       // ← store ID here
          _rowName: selectedRow.name,   // ← store name here
        });
        setDuplicateDialogOpen(true);
      }
      handleMenuClose();
    };

    const handleDuplicateSubmit = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/activity-reports/requests/duplicate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: duplicateForm._rowId,           // ← use stored ID
            report_name: duplicateForm.title,
            period_start_date: duplicateForm.period_start_date,
            start_date: duplicateForm.period_start_date,
            period_end_date: duplicateForm.period_end_date,
            end_date: duplicateForm.period_end_date,
            due_date: duplicateForm.due_date,
          }),
        });

        if (!res.ok) throw new Error('Failed to duplicate');

        setDuplicateDialogOpen(false);
        fetch2ReportsData();
        setSnackbar2({
          open: true,
          severity: 'success',
          title: 'Report Duplicated',
          message: `"${duplicateForm._rowName}" has been duplicated successfully.`,  // ← use stored name
          actionButtons: [],
        });
      } catch (err) {
        console.error('Duplicate failed:', err);
        setSnackbar2({
          open: true,
          severity: 'error',
          title: 'Duplicate Failed',
          message: 'Something went wrong. Please try again.',
          actionButtons: [],
        });
      }
    };

    // Snackbar close handler
    const handleSnackbarClose = (event, reason) => {
      // Prevent closing by clicking away if you want
      if (reason === "clickaway") {
        return;
      }
      setSnackbar((prev) => ({ ...prev, open: false }));
    };

    /* =======================
    4. COLUMNS
    ======================= */

    useImperativeHandle(ref, () => ({
      openColumnsPanel: () => {
        apiRef.current?.showPreferences("columns");
      },
      openFiltersPanel: () => {
        apiRef.current?.showPreferences("filters");
      },
      setStatusFilter: () => {
        const currentAchievedStatus =mapActivityRequestStatusCodeToLabel(1, role);
        console.log("Setting status filter for: ", currentAchievedStatus);
        apiRef.current?.setFilterModel({
          items: [
            {
              field: "status",
              operator: "isAnyOf",
              value: [
                "Draft",
                "Scheduled",
                "New Request",
                "Overdue",
                "In Progress",
                currentAchievedStatus
              ],
            },
          ],
        });
      },
      clearStatusFilter: () => {
        apiRef.current?.setFilterModel({ items: [] });
      },
    }));

    const flagButtonStyles = {
      padding: 2,
      paddingInline: 1,
      margin: 0,
      minWidth: "0px",
      position: "relative",
      left: "2px",
      borderRadius: "80%",
      height: "24px",
      marginLeft: "5px",
    };

    const columns = [
      {
        field: "quarter",
        headerName: "Quarter",
        flex: 0.8,
      },
      {
        field: "request",
        headerName: "Request",
        flex: 0.8,
        renderCell: (params) => {
          return (
            <Chip
              label={params.value}
              sx={{
                backgroundColor: "white",
                boxShadow:
                  "0px 1px 3px rgba(0, 0, 0, 0.2), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 2px 1px rgba(0, 0, 0, 0.12)",
                color: "#757677",
                fontWeight: 500,
                fontSize: "0.875rem",
                width: "90px",
                textAlign: "center",
              }}
            />
          );
        },
      },
      {
        field: "name",
        headerName: "Name",
        flex: 1.5,
      },
      {
        field: "period",
        headerName: "Period",
        flex: 1,
      },
      {
        field: "sent",
        headerName: role === "partner" ? "Received" : "Sent",
        flex: 0.75,
      },
      {
        field: "dueDate",
        headerName: "Due Date",
        flex: 0.75,
      },
      {
        field: "completionDate",
        headerName: "Completion Date",
        flex: 0.75,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        renderCell: (params) => {
          const styles = getStatusColor(params.value);
          return (
            <Chip
              label={params.value}
              sx={{
                backgroundColor: styles.backgroundColor,
                color: styles.color,
                fontWeight: 500,
                fontSize: "0.875rem",
              }}
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "",
        flex: 1,
        sortable: false,
        filterable: false,
        fixed: true,
        renderCell: (params) => {
          const isDisabled =
            params.row.status === "In Progress" ||
            params.row.status === "Draft" ||
            params.row.status === "Overdue";

          return (
            <>
              <CommonButton
                variant="contained"
                size="medium"
                color="secondary"
                disabled={openingReportId === params.row.id}
                sx={{ height: "34px", width: "40px", padding: 1, opacity: openingReportId === params.row.id ? 0.8 : 1, }}
                onClick={async (e) => {
                  e.stopPropagation();
                  setOpeningReportId(params.row.id);

                  try {
                    const [companiesRes, requestDataRes] = await Promise.all([
                      fetch(`${API_BASE_URL}/activity-reports/requests/${params.row.id}/companies`),
                      fetch(`${API_BASE_URL}/activity-reports/requests/${params.row.id}`)
                    ]);

                    const companiesJson = await companiesRes.json();
                    const requestDataJson = await requestDataRes.json();

                    console.log('companiesJson:', companiesJson);
                    console.log('requestDataJson:', requestDataJson);
                    setOpeningReportId(null);
                    
                    const requestItem = requestDataJson?.data ?? requestDataJson;
                    navigate(`/grade-reports/${params.row.id}`, {
                      state: {
                        reportName: params.row.name,
                        statusType: params.row.status,
                        requestId: params.row.id,
                        requestStatusCode: requestItem?.status,
                        request_type: params.row.request,
                        settingsData: requestItem || params.row.settingsData || params.row,
                        companiesData: companiesJson.data || [],
                        reportData: requestItem?.request_data || null,
                      },
                    });

                  } catch (err) {
                    console.error('Error fetching request data:', err);
                    setOpeningReportId(null);
                    navigate(`/grade-reports/${params.row.id}`, {
                      state: {
                        reportName: params.row.name,
                        statusType: params.row.status,
                        requestId: params.row.id,
                        request_type: params.row.request,
                        settingsData: params.row.settingsData || params.row,
                        companiesData: [],
                        reportData: null,
                      },
                    });
                  }
                }}
              // disabled={isDisabled}
              >
                {openingReportId === params.row.id ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={14} color="inherit" />
                    Open.
                  </Box>
                ) : (
                  "Open"
                )}
              </CommonButton>


              {role !== "partner" && (
                <CommonButton
                  variant="text"
                  sx={flagButtonStyles}
                  onClick={(e) => handleMenuClick(e, params.row)}
                  aria-label={`Actions for ${params.row.name}`}
                >
                  <ListItemIcon sx={{ minWidth: "0px" }}>
                    <MoreVertIcon fontSize="small" />
                  </ListItemIcon>
                </CommonButton>
              )}

              {/* {role === "partner" && (
                <CommonButton
                  variant="contained"
                  color="secondary"
                  sx={flagButtonStyles}
                  onClick={(e) => navigate(`/shared-requests/1`, {
                    state: {
                      reportName: params.row.name,
                    },
                  })}
                  aria-label={`Actions for ${params.row.name}`}
                >
                  <ListItemIcon sx={{ minWidth: "0px" }}>
                    <SupervisorAccountIcon fontSize="small" />
                  </ListItemIcon>
                </CommonButton>
              )} */}

            </>
          );
        },
      },
    ];

    /* =======================
    5. RENDER
    ======================= */

    return (
      <Box
        sx={{
          height: "80vh",
          ...(role === "partner" && { height: "20vh" }), // Adjust height for partner role to accommodate the second table
          maxHeight: 800,
          minHeight: 300,
          width: "100%",
        }}
      >
        <DataTable
          ref={tableRef}
          apiRef={apiRef}
          rows={filteredReports}
          loading={isLoading}   // ADD THIS
          columns={columns}
          initialState={{
            pinnedColumns: { right: ["actions"] },
          }}
          hideFooterPagination={true}
          hideFooter={true}
          sx={{
            height: "100%",
          }}
          hideHeader={!showHeader}
        />

        {/* Context Menu */}
        <StyledMenu
          id="row-action-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          MenuListProps={{
            "aria-labelledby": "action-button",
          }}
        >
          <MenuItem disableRipple>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
              Actions
            </Typography>
          </MenuItem>
          <MenuItem onClick={handleDuplicate} disableRipple>
            <FileCopyIcon />
            Duplicate
          </MenuItem>
          {/*  <MenuItem onClick={handleMarkAsComplete} disableRipple>
            <LibraryAddCheckIcon />
            Mark as Complete
          </MenuItem>
          <MenuItem onClick={handleEdit} disableRipple>
            <EditIcon />
            Edit
          </MenuItem> */}
        </StyledMenu>

        {/* Success/Error Snackbar */}
        <StyledSnackbarWithTitle
          open={snackbar2.open}
          onClose={() => setSnackbar2((prev) => ({ ...prev, open: false }))}
          autoHideDuration={6000}
          anchorOrigin={anchorOrigin}
          severity={snackbar2.severity}
          sx={{
            backgroundColor: '#fff',
            maxWidth: '380px',
            boxShadow: 3,
            borderRadius: 2,
            border: '1px solid #E0E3E5',
            padding: 1,
          }}
        >
          <SnackbarContent>
            {showIcon && IconComponent && (
              <Box sx={{ mt: 0.5 }}>
                <IconComponent sx={{ color: getIconColor(), fontSize: 18 }} />
              </Box>
            )}

            <ContentWrapper>
              {/* Title Section */}
              {snackbar2.title && (
                <TitleTypography sx={{ color: '#1A1919' }} variant="subtitle1">
                  {snackbar2.title}
                </TitleTypography>
              )}

              {/* Message Section */}
              {snackbar2.message && (
                <MessageTypography variant="body2">
                  {snackbar2.message}
                </MessageTypography>
              )}

              {/* Action Buttons Section */}
              <ActionWrapper>
                {/* Custom action buttons */}
                {actionButtons.map((button, index) => (
                  <Button
                    key={index}
                    size="small"
                    variant={button.variant || 'text'}
                    color={button.color || 'primary'}
                    onClick={button.onClick}
                    sx={{
                      minWidth: 'auto',
                      fontSize: '0.75rem',
                      ...button.sx
                    }}
                  >
                    {button.label}
                  </Button>
                ))}

                {/* Default undo button */}
                {onUndo && (
                  <Button
                    size="small"
                    color="secondary"
                    variant="contained"
                    onClick={onUndo}
                    sx={{ fontSize: '0.75rem', mb: 0.5 }}
                  >
                    UNDO
                  </Button>
                )}

                {/* Retry button */}
                {onRetry && (
                  <Button
                    size="small"
                    color="secondary"
                    onClick={onRetry}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    RETRY
                  </Button>
                )}
              </ActionWrapper>
            </ContentWrapper>

            {/* Close Button */}
            <IconButton
              size="small"
              aria-label="close"
              onClick={() => setSnackbar2((prev) => ({ ...prev, open: false }))}
              sx={{
                ml: 1,
                mt: 0.5,
                color: 'text.secondary',
                '&:hover': {
                  color: 'text.primary',
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </SnackbarContent>
        </StyledSnackbarWithTitle>
        {/* Duplicate Dialog */}
        <Dialog
          open={duplicateDialogOpen}
          onClose={() => setDuplicateDialogOpen(false)}
          PaperProps={{ sx: { borderRadius: "16px", p: 1, width: "440px", maxWidth: "95vw" } }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 3, pt: 2, pb: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "17px" }}>Duplicate report</Typography>
            <IconButton size="small" onClick={() => setDuplicateDialogOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <DialogContent sx={{ px: 3, pt: 1 }}>
            <Typography sx={{ fontSize: "13px", color: "#4B5563", mb: 2, lineHeight: 1.6 }}>
              Duplicating <strong style={{ color: "#1A1919", fontWeight: 500 }}>{duplicateForm._rowName}</strong>. Update the details for the new copy below.
            </Typography>

            {/* Title */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>Report title</Typography>
              <TextField
                fullWidth
                size="small"
                value={duplicateForm.title}
                onChange={(e) => setDuplicateForm(prev => ({ ...prev, title: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Box>

            {/* Due Date */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>Due date</Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={duplicateForm.due_date}
                onChange={(e) => setDuplicateForm(prev => ({ ...prev, due_date: e.target.value }))}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
              />
            </Box>

            {/* Duration */}
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 0.5 }}>Duration</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                <Box>
                  <Typography sx={{ fontSize: "11px", color: "#9CA3AF", mb: 0.5 }}>Start date</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={duplicateForm.period_start_date}
                    onChange={(e) => setDuplicateForm(prev => ({ ...prev, period_start_date: e.target.value }))}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "11px", color: "#9CA3AF", mb: 0.5 }}>End date</Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    value={duplicateForm.period_end_date}
                    onChange={(e) => setDuplicateForm(prev => ({ ...prev, period_end_date: e.target.value }))}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                </Box>
              </Box>
            </Box>

            <Box sx={{ bgcolor: "#F3F4F6", borderRadius: "8px", p: 1.5, display: "flex", gap: 1 }}>
              <Typography sx={{ fontSize: "12px", color: "#6B7280", lineHeight: 1.5 }}>
                ℹ Companies and report settings will be copied from the original. The new report will be saved as a <strong style={{ fontWeight: 500 }}>Draft</strong>.
              </Typography>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
            <Button
              onClick={() => setDuplicateDialogOpen(false)}
              sx={{ textTransform: "none", color: "#6B7280", fontWeight: 500 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDuplicateSubmit}
              variant="contained"
              sx={{ textTransform: "none", bgcolor: "#1E517B", borderRadius: "8px", fontWeight: 600 }}
            >
              Duplicate
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  },
);

export default ReportsTable;
